<template>
  <div class="main-layout">
    <!-- 顶部导航栏 -->
    <el-header class="main-header" height="60px">
      <div class="header-left">
        <div class="logo-section">
          <img src="@/assets/7d-logo.png" alt="7D Logo" class="header-logo" />
          <span class="system-title">{{ $t('layout.systemTitle') }}</span>
        </div>
      </div>
      
      <div class="header-center">
        <div class="breadcrumb-nav">
          <el-breadcrumb separator="/">
            <el-breadcrumb-item 
              :to="{ path: '/dashboard' }" 
              v-if="$route.path !== '/dashboard'"
            >{{ $t('layout.home') }}</el-breadcrumb-item>
            <el-breadcrumb-item v-if="$route.path === '/dashboard'">
              {{ $t('layout.home') }}
            </el-breadcrumb-item>
            <el-breadcrumb-item 
              v-if="currentModule && !$route.path.startsWith(currentModule.path)" 
              :to="{ path: currentModule.path }"
            >
              {{ currentModule.title }}
            </el-breadcrumb-item>
            <el-breadcrumb-item v-if="currentModule && $route.path.startsWith(currentModule.path)">
              {{ currentModule.title }}
            </el-breadcrumb-item>
            <el-breadcrumb-item 
              v-if="currentPage && $route.path !== currentPage.path" 
              :to="{ path: currentPage.path }"
            >
              {{ currentPage.title }}
            </el-breadcrumb-item>
            <el-breadcrumb-item v-if="currentPage && $route.path === currentPage.path">
              {{ currentPage.title }}
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>
      </div>
      
      <div class="header-right">
        <div class="user-info">
          <el-dropdown @command="handleUserCommand">
            <span class="user-dropdown">
              <el-avatar :size="32" :src="getFullUrl(currentUser ? currentUser.avatar : '')">
                <i class="el-icon-user-solid"></i>
              </el-avatar>
              <span class="username">{{ currentUser ? currentUser.username : 'Unknown' }}</span>
              <i class="el-icon-arrow-down"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">{{ $t('layout.profile') }}</el-dropdown-item>
              <el-dropdown-item command="settings">{{ $t('layout.settings') }}</el-dropdown-item>
              <el-dropdown-item divided command="logout">{{ $t('layout.logout') }}</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </el-header>

    <el-container class="main-container">
      <!-- 移动端遮罩层 -->
      <div 
        v-if="isMobile && !sidebarCollapsed" 
        class="sidebar-overlay" 
        @click="sidebarCollapsed = true"
      ></div>

      <!-- 左侧导航栏 -->
      <el-aside 
        class="main-sidebar" 
        :class="{ 
          'show': isMobile && !sidebarCollapsed,
          'sidebar-collapsed': sidebarCollapsed 
        }"
        :width="sidebarCollapsed ? (isMobile ? '0px' : '64px') : '240px'"
      >
        <el-menu
          ref="sidebarMenu"
          :default-active="activeMenu"
          class="sidebar-menu"
          :collapse="sidebarCollapsed"
          :unique-opened="true"
          :collapse-transition="false"
          :default-openeds="defaultOpeneds"
          router
          @select="handleMenuSelect"
        >
          <!-- 首页/仪表盘 -->
          <el-menu-item index="dashboard" route="/dashboard" v-if="canAccessRoute('/dashboard')">
            <i class="el-icon-odometer"></i>
            <span slot="title">{{ $t('layout.home') }}</span>
          </el-menu-item>

          <!-- 数据分析模块 -->
          <el-submenu index="analysis" v-if="hasAnyRouteAccess(['/analysis/overview', '/analysis/user', '/analysis/points', '/analysis/product', '/analysis/collection', '/analysis/badge', '/analysis/activity'])">
            <template slot="title">
              <i class="el-icon-pie-chart"></i>
              <span slot="title">{{ $t('analysis.dataAnalysis') }}</span>
            </template>
            <el-menu-item index="analysis-overview" route="/analysis/overview" v-if="canAccessRoute('/analysis/overview')">
              <i class="el-icon-data-board"></i>
              <span slot="title">{{ $t('analysis.overview') }}</span>
            </el-menu-item>
            <el-menu-item index="analysis-user" route="/analysis/user" v-if="canAccessRoute('/analysis/user')">
              <i class="el-icon-user"></i>
              <span slot="title">{{ $t('analysis.userAnalysis') }}</span>
            </el-menu-item>
            <el-menu-item index="analysis-points" route="/analysis/points" v-if="canAccessRoute('/analysis/points')">
              <i class="el-icon-coin"></i>
              <span slot="title">{{ $t('analysis.pointsAnalysis') }}</span>
            </el-menu-item>
            <el-menu-item index="analysis-product" route="/analysis/product" v-if="canAccessRoute('/analysis/product')">
              <i class="el-icon-goods"></i>
              <span slot="title">{{ $t('analysis.productAnalysis') }}</span>
            </el-menu-item>
            <el-menu-item index="analysis-collection" route="/analysis/collection" v-if="canAccessRoute('/analysis/collection')">
              <i class="el-icon-picture-outline"></i>
              <span slot="title">{{ $t('analysis.collectionAnalysis') }}</span>
            </el-menu-item>
            <el-menu-item index="analysis-badge" route="/analysis/badge" v-if="canAccessRoute('/analysis/badge')">
              <i class="el-icon-medal"></i>
              <span slot="title">{{ $t('analysis.badgeAnalysis') }}</span>
            </el-menu-item>
            <el-menu-item index="analysis-activity" route="/analysis/activity" v-if="canAccessRoute('/analysis/activity')">
              <i class="el-icon-data-line"></i>
              <span slot="title">{{ $t('analysis.userActivity') }}</span>
            </el-menu-item>
          </el-submenu>

          <!-- 用户管理模块 -->
          <el-submenu index="user" v-if="hasAnyRouteAccess(['/user', '/user/group', '/user/role', '/user/permission', '/user/points'])">
            <template slot="title">
              <i class="el-icon-user"></i>
              <span slot="title">{{ $t('layout.userManagement') }}</span>
            </template>
            <el-menu-item index="user-list" route="/user" v-if="canAccessRoute('/user')">
              <i class="el-icon-tickets"></i>
              <span slot="title">{{ $t('layout.userList') }}</span>
            </el-menu-item>
            <el-menu-item index="user-group" route="/user/group" v-if="canAccessRoute('/user/group')">
              <i class="el-icon-s-cooperation"></i>
              <span slot="title">{{ $t('layout.userGroup') }}</span>
            </el-menu-item>
            <el-menu-item index="level-management" route="/user/level" v-if="canAccessRoute('/user/level')">
              <i class="el-icon-star-off"></i>
              <span slot="title">等级管理</span>
            </el-menu-item>
            <el-menu-item index="points-management" route="/user/points" v-if="canAccessRoute('/user/points')">
              <i class="el-icon-coin"></i>
              <span slot="title">积分管理</span>
            </el-menu-item>
            <el-menu-item index="user-role" route="/user/role" v-if="canAccessRoute('/user/role')">
              <i class="el-icon-medal"></i>
              <span slot="title">{{ $t('layout.rolePermissions') }}</span>
            </el-menu-item>
            <el-menu-item index="user-permission" route="/user/permission" v-if="canAccessRoute('/user/permission')">
              <i class="el-icon-lock"></i>
              <span slot="title">{{ $t('layout.permissionManagement') }}</span>
            </el-menu-item>
          </el-submenu>

          <!-- 产品管理模块 -->
          <el-submenu index="product" v-if="hasAnyRouteAccess(['/product', '/product/digital-collection', '/product/series', '/product/sku-manage', '/product/sku-rule'])">
            <template slot="title">
              <i class="el-icon-goods"></i>
              <span slot="title">产品管理</span>
            </template>
            <el-menu-item index="product-list" route="/product" v-if="canAccessRoute('/product')">
              <i class="el-icon-tickets"></i>
              <span slot="title">产品列表</span>
            </el-menu-item>
            <el-menu-item index="digital-collection" route="/product/digital-collection" v-if="canAccessRoute('/product/digital-collection')">
              <i class="el-icon-picture-outline"></i>
              <span slot="title">数字藏品</span>
            </el-menu-item>
            <el-menu-item index="series-list" route="/product/series" v-if="canAccessRoute('/product/series')">
              <i class="el-icon-collection"></i>
              <span slot="title">系列管理</span>
            </el-menu-item>
            <el-menu-item index="sku-manage" route="/product/sku-manage" v-if="canAccessRoute('/product/sku-manage')">
              <i class="el-icon-s-grid"></i>
              <span slot="title">SKU管理</span>
            </el-menu-item>
            <el-menu-item index="sku-rule" route="/product/sku-rule" v-if="canAccessRoute('/product/sku-rule')">
              <i class="el-icon-setting"></i>
              <span slot="title">SKU规则</span>
            </el-menu-item>
          </el-submenu>

          <!-- 设备管理模块 -->
          <el-submenu index="device" v-if="hasAnyRouteAccess(['/device', '/device/group', '/device/peripheral/list', '/device/ota'])">
            <template slot="title">
              <i class="el-icon-mobile-phone"></i>
              <span slot="title">{{ $t('layout.deviceManagement') }}</span>
            </template>
            <el-menu-item index="device-list" route="/device" v-if="canAccessRoute('/device')">
              <i class="el-icon-connection"></i>
              <span slot="title">{{ $t('layout.deviceList') }}</span>
            </el-menu-item>
            <el-menu-item index="peripheral-list" route="/device/peripheral/list" v-if="canAccessRoute('/device/peripheral/list')">
              <i class="el-icon-bank-card"></i>
              <span slot="title">NFC管理</span>
            </el-menu-item>
            <el-menu-item index="device-ota" route="/device/ota" v-if="canAccessRoute('/device/ota')">
              <i class="el-icon-upload"></i>
              <span slot="title">OTA管理</span>
            </el-menu-item>
          </el-submenu>

          <!-- Agent管理模块 -->
          <el-submenu index="agent" v-if="hasAnyRouteAccess(['/agent/list', '/agent/template', '/agent/plugin', '/agent/chat'])">
            <template slot="title">
              <i class="el-icon-s-custom"></i>
              <span slot="title">{{ $t('layout.agentManagement') }}</span>
            </template>
            <el-menu-item index="agent-list" route="/agent/list" v-if="canAccessRoute('/agent/list')">
              <i class="el-icon-user"></i>
              <span slot="title">智能体列表</span>
            </el-menu-item>
            <el-menu-item index="agent-template" route="/agent/template" v-if="canAccessRoute('/agent/template')">
              <i class="el-icon-collection"></i>
              <span slot="title">智能体模板</span>
            </el-menu-item>
            <el-menu-item index="agent-plugin" route="/agent/plugin" v-if="canAccessRoute('/agent/plugin')">
              <i class="el-icon-connection"></i>
              <span slot="title">智能体插件</span>
            </el-menu-item>
            <el-menu-item index="agent-chat" route="/agent/chat" v-if="canAccessRoute('/agent/chat')">
              <i class="el-icon-chat-dot-square"></i>
              <span slot="title">聊天记录</span>
            </el-menu-item>
          </el-submenu>

          <!-- 资产管理模块 -->
          <el-submenu index="cms" v-if="canAccessCms">
            <template slot="title">
              <i class="el-icon-folder-opened"></i>
              <span slot="title">资产管理</span>
            </template>
            <el-menu-item index="cms-asset-list" route="/cms/assets" v-if="canAccessRoute('/cms/assets')">
              <i class="el-icon-files"></i>
              <span slot="title">资产列表</span>
            </el-menu-item>
            <el-menu-item index="cms-theme-list" route="/cms/themes" v-if="canAccessRoute('/cms/themes')">
              <i class="el-icon-collection"></i>
              <span slot="title">主题管理</span>
            </el-menu-item>
            <el-menu-item index="cms-tag-management" route="/cms/tags" v-if="canAccessRoute('/cms/tags')">
              <i class="el-icon-collection-tag"></i>
              <span slot="title">标签管理</span>
            </el-menu-item>
            <el-menu-item index="light-effect-list" route="/light/effect" v-if="canAccessRoute('/light/effect')">
              <i class="el-icon-sunny"></i>
              <span slot="title">灯效管理</span>
            </el-menu-item>
            <el-menu-item index="cms-document-list" route="/cms/documents" v-if="canAccessRoute('/cms/documents')">
              <i class="el-icon-document"></i>
              <span slot="title">文档管理</span>
            </el-menu-item>
          </el-submenu>

          <!-- 资源分配与监控模块 -->
          <el-submenu index="resource" v-if="hasAnyRouteAccess(['/resource/dashboard', '/resource/models', '/resource/provider', '/resource/quota', '/resource/quota/policy', '/resource/quota/logs', '/resource/report'])">
            <template slot="title">
              <i class="el-icon-cpu"></i>
              <span slot="title">{{ $t('layout.resourceManagement') }}</span>
            </template>
            <el-menu-item index="resource-dashboard" route="/resource/dashboard" v-if="canAccessRoute('/resource/dashboard')">
              <i class="el-icon-data-analysis"></i>
              <span slot="title">{{ $t('layout.resourceOverview') }}</span>
            </el-menu-item>
            <el-menu-item index="model-list" route="/resource/models" v-if="canAccessRoute('/resource/models')">
              <i class="el-icon-cpu"></i>
              <span slot="title">{{ $t('layout.modelManagement') }}</span>
            </el-menu-item>
            <el-menu-item index="model-provider" route="/resource/provider" v-if="canAccessRoute('/resource/provider')">
              <i class="el-icon-connection"></i>
              <span slot="title">{{ $t('providerManagement.pageTitle') }}</span>
            </el-menu-item>
            <el-menu-item index="resource-quota" route="/resource/quota" v-if="canAccessRoute('/resource/quota')">
              <i class="el-icon-s-cooperation"></i>
              <span slot="title">{{ $t('layout.quotaManagement') }}</span>
            </el-menu-item>
            <el-menu-item index="quota-policy" route="/resource/quota/policy" v-if="canAccessRoute('/resource/quota/policy')">
              <i class="el-icon-set-up"></i>
              <span slot="title">{{ $t('resourceManagement.quotaPolicy') }}</span>
            </el-menu-item>
            <el-menu-item index="quota-logs" route="/resource/quota/logs" v-if="canAccessRoute('/resource/quota/logs')">
              <i class="el-icon-document-copy"></i>
              <span slot="title">{{ $t('resourceManagement.operationLog') }}</span>
            </el-menu-item>
            <el-menu-item index="resource-report" route="/resource/report" v-if="canAccessRoute('/resource/report')">
              <i class="el-icon-document"></i>
              <span slot="title">{{ $t('layout.usageReport') }}</span>
            </el-menu-item>
          </el-submenu>

          <!-- 活动管理模块 -->
          <el-submenu index="activity" v-if="hasAnyRouteAccess(['/activity/community', '/activity/memorial', '/activity/shop'])">
            <template slot="title">
              <i class="el-icon-trophy"></i>
              <span slot="title">{{ $t('layout.activityManagement') }}</span>
            </template>
            <el-menu-item index="activity-community" route="/activity/community" v-if="canAccessRoute('/activity/community')">
              <i class="el-icon-chat-dot-round"></i>
              <span slot="title">{{ $t('layout.communityActivity') }}</span>
            </el-menu-item>
            <el-menu-item index="activity-memorial" route="/activity/memorial" v-if="canAccessRoute('/activity/memorial')">
              <i class="el-icon-present"></i>
              <span slot="title">{{ $t('layout.activityMemorial') }}</span>
            </el-menu-item>
            <el-menu-item index="activity-shop" route="/activity/shop" v-if="canAccessRoute('/activity/shop')">
              <i class="el-icon-goods"></i>
              <span slot="title">{{ $t('layout.rightsShop') }}</span>
            </el-menu-item>
            <el-menu-item index="activity-exchange" route="/activity/shop/exchange" v-if="canAccessRoute('/activity/shop/exchange')">
              <i class="el-icon-s-order"></i>
              <span slot="title">{{ $t('layout.rightsExchange') }}</span>
            </el-menu-item>
          </el-submenu>

          <!-- 售后服务模块 -->
          <el-submenu index="aftersales" v-if="hasAnyRouteAccess(['/aftersales/consultation'])">
            <template slot="title">
              <i class="el-icon-service"></i>
              <span slot="title">售后服务</span>
            </template>
            <el-menu-item index="aftersales-consultation" route="/aftersales/consultation" v-if="canAccessRoute('/aftersales/consultation')">
              <i class="el-icon-chat-line-square"></i>
              <span slot="title">客诉管理</span>
            </el-menu-item>
          </el-submenu>

           <!-- 系统管理模块 -->
          <el-submenu index="system" v-if="hasAnyRouteAccess(['/system/log', '/system/notification', '/system/alarm', '/system/monitor', '/system/job', '/system/dict', '/system/params', '/system/config'])">
            <template slot="title">
              <i class="el-icon-setting"></i>
              <span slot="title">{{ $t('layout.systemManagement') }}</span>
            </template>
            <el-menu-item index="system-log" route="/system/log" v-if="canAccessRoute('/system/log')">
              <i class="el-icon-tickets"></i>
              <span slot="title">{{ $t('layout.operationLog') }}</span>
            </el-menu-item>
            <el-menu-item index="system-notification" route="/system/notification" v-if="canAccessRoute('/system/notification')">
              <i class="el-icon-bell"></i>
              <span slot="title">{{ $t('layout.notificationManagement') }}</span>
            </el-menu-item>
            <el-menu-item index="system-alarm" route="/system/alarm" v-if="canAccessRoute('/system/alarm')">
              <i class="el-icon-warning"></i>
              <span slot="title">{{ $t('layout.alarmManagement') }}</span>
            </el-menu-item>
            <el-menu-item index="system-monitor" route="/system/monitor" v-if="canAccessRoute('/system/monitor')">
              <i class="el-icon-monitor"></i>
              <span slot="title">{{ $t('layout.serverMonitor') }}</span>
            </el-menu-item>
            <el-menu-item index="system-job" route="/system/job" v-if="canAccessRoute('/system/job')">
              <i class="el-icon-time"></i>
              <span slot="title">{{ $t('layout.jobManagement') }}</span>
            </el-menu-item>
            <el-menu-item index="system-dict" route="/system/dict" v-if="canAccessRoute('/system/dict')">
              <i class="el-icon-notebook-2"></i>
              <span slot="title">{{ $t('dictManagement.pageTitle') }}</span>
            </el-menu-item>
            <el-menu-item index="system-params" route="/system/params" v-if="canAccessRoute('/system/params')">
              <i class="el-icon-setting"></i>
              <span slot="title">{{ $t('paramManagement.pageTitle') }}</span>
            </el-menu-item>
            <el-menu-item index="system-config" route="/system/config" v-if="canAccessRoute('/system/config')">
              <i class="el-icon-setting"></i>
              <span slot="title">{{ $t('layout.systemConfig') }}</span>
            </el-menu-item>
          </el-submenu>
        </el-menu>

        <!-- 折叠按钮 - 移到菜单最下方 -->
        <div class="sidebar-toggle-btn" @click="toggleSidebar" :title="sidebarCollapsed ? $t('layout.expandMenu') : $t('layout.collapseMenu')">
          <i :class="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
        </div>
      </el-aside>

      <!-- 右侧内容区域 -->
      <el-main class="main-content" ref="mainContent">
        <div class="content-header">
          <h1 class="page-title">{{ currentPage?.title || $t('dashboard.title') }}</h1>
          <div class="page-actions" v-if="currentPage?.actions">
            <el-button
              v-for="action in currentPage.actions"
              :key="action.key"
              :type="action.type || 'primary'"
              :icon="action.icon"
              @click="handlePageAction(action)"
            >
              {{ action.text }}
            </el-button>
          </div>
        </div>
        
        <div class="content-body">
          <keep-alive :include="cachedViews">
            <router-view :key="$route.fullPath" />
          </keep-alive>
        </div>
      </el-main>
    </el-container>


  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { hasPermission } from '@/utils/permission'
import { getFullUrl } from '@/utils/index'
import { getUser } from '@/utils/auth'

export default {
  name: 'MainLayout',
  data() {
    return {
      sidebarCollapsed: false,
      isMobile: false,
      permissionSyncTimer: null,
      // 模块配置
      moduleConfig: {
        user: {
          title: '',
          path: '/user'
        },
        analysis: {
          title: '',
          path: '/analysis/overview'
        },
        device: {
          title: '',
          path: '/device'
        },
        peripheral: {
          title: 'NFC管理',
          path: '/device/peripheral/list'
        },
        agent: {
          title: '',
          path: '/agent'
        },
        resource: {
          title: '',
          path: '/resource'
        },
        cms: {
          title: '资产管理',
          path: '/cms/assets'
        },
        sku: {
          title: 'SKU管理',
          path: '/product/sku-manage'
        },
        skuRule: {
          title: 'SKU规则',
          path: '/product/sku-rule'
        },
        product: {
          title: '产品管理',
          path: '/product'
        },
        system: {
          title: '',
          path: '/system'
        }
      }
    }
  },
  created() {
    // 1. 初始化模块配置中的标题
    this.moduleConfig.user.title = this.$t('layout.userManagement');
    this.moduleConfig.analysis.title = this.$t('analysis.dataAnalysis');
    this.moduleConfig.device.title = this.$t('layout.deviceManagement');
    this.moduleConfig.peripheral.title = 'NFC管理';
    this.moduleConfig.agent.title = this.$t('layout.agentManagement');
    this.moduleConfig.resource.title = this.$t('layout.resourceManagement');
    this.moduleConfig.cms.title = '资产管理';
    this.moduleConfig.system.title = this.$t('layout.systemManagement');
    
    // 2. 如果 Vuex 中没有用户信息，尝试从本地加载
    if (!this.currentUser || !this.currentUser.username) {
      const user = getUser()
      if (user) {
        this.$store.commit('user/SET_CURRENT_USER', user)
      }
    }

    this.syncPermissions()
    this.checkMobile();
  },
  mounted() {
    window.addEventListener('resize', this.checkMobile);
    this.permissionSyncTimer = setInterval(() => {
      this.syncPermissions()
    }, 5 * 60000) // 5分钟同步一次权限
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
    if (this.permissionSyncTimer) {
      clearInterval(this.permissionSyncTimer)
      this.permissionSyncTimer = null
    }
  },
  computed: {
    ...mapState('user', ['currentUser']),
    canAccessCms() {
      return this.hasAnyRouteAccess(['/cms/assets', '/cms/themes', '/cms/tags', '/cms/documents', '/light/effect'])
    },
    cmsDefaultPath() {
      if (this.hasPermission('cms:asset:list')) return '/cms/assets'
      if (this.hasPermission('cms:theme:list')) return '/cms/themes'
      return '/cms/assets'
    },
    
    activeMenu() {
      const route = this.$route
      const { meta, path } = route
      
      // 1. 如果路由配置了显式的激活菜单项，则优先使用
      if (meta && meta.activeMenu) {
        return meta.activeMenu
      }
      
      // 2. 精确匹配
      const menuMap = {
        '/dashboard': 'dashboard',
        '/analysis/overview': 'analysis-overview',
        '/analysis/user': 'analysis-user',
        '/analysis/points': 'analysis-points',
        '/analysis/product': 'analysis-product',
        '/analysis/collection': 'analysis-collection',
        '/analysis/badge': 'analysis-badge',
        '/analysis/activity': 'analysis-activity',
        '/user': 'user-list',
        '/user/group': 'user-group',
        '/user/level': 'level-management',
        '/user/points': 'points-management',
        '/user/role': 'user-role',
        '/user/permission': 'user-permission',
        '/device': 'device-list',
        '/device/peripheral/list': 'peripheral-list',
        '/agent/list': 'agent-list',
        '/agent/template': 'agent-template',
        '/agent/plugin': 'agent-plugin',
        '/agent/chat': 'agent-chat',
        '/resource/dashboard': 'resource-dashboard',
        '/resource/quota': 'resource-quota',
        '/resource/quota/policy': 'quota-policy',
        '/resource/quota/logs': 'quota-logs',
        '/resource/report': 'resource-report',
        '/resource/models': 'model-list',
        '/resource/provider': 'model-provider',
        '/device/ota': 'device-ota',
        '/cms/assets': 'cms-asset-list',
        '/cms/themes': 'cms-theme-list',
        '/cms/tags': 'cms-tag-management',
        '/light/effect': 'light-effect-list',
        '/cms/documents': 'cms-document-list',
        '/system/log': 'system-log',
        '/system/notification': 'system-notification',
        '/system/alarm': 'system-alarm',
        '/system/monitor': 'system-monitor',
        '/system/job': 'system-job',
        '/system/dict': 'system-dict',
        '/system/params': 'system-params',
        '/system/config': 'system-config',
        '/aftersales/consultation': 'aftersales-consultation',
        '/activity/community': 'activity-community',
        '/activity/memorial': 'activity-memorial',
        '/activity/shop': 'activity-shop',
        '/activity/shop/exchange': 'activity-exchange',
        '/product/digital-collection': 'digital-collection',
        '/product/series': 'series-list',
        '/product': 'product-list'
      }

      if (menuMap[path]) return menuMap[path]

      // 3. 模糊匹配（处理详情页等子路由），注意顺序：先匹配更长的路径以保证准确性
      if (path.startsWith('/analysis/overview')) return 'analysis-overview'
      if (path.startsWith('/analysis/user')) return 'analysis-user'
      if (path.startsWith('/analysis/points')) return 'analysis-points'
      if (path.startsWith('/analysis/product')) return 'analysis-product'
      if (path.startsWith('/analysis/collection')) return 'analysis-collection'
      if (path.startsWith('/analysis/badge')) return 'analysis-badge'
      if (path.startsWith('/analysis/activity')) return 'analysis-activity'
      if (path.startsWith('/user/group')) return 'user-group'
      if (path.startsWith('/user/level')) return 'level-management'
      if (path.startsWith('/user/points')) return 'points-management'
      if (path.startsWith('/user/role')) return 'user-role'
      if (path.startsWith('/user')) return 'user-list'
      if (path.startsWith('/product/sku-manage')) return 'sku-manage'
      if (path.startsWith('/product/sku-rule')) return 'sku-rule'
      if (path.startsWith('/product/digital-collection')) return 'digital-collection'
      if (path.startsWith('/product/series')) return 'series-list'
      if (path.startsWith('/product')) return 'product-list'
      
      if (path.startsWith('/device')) {
        if (path.includes('/peripheral/list')) return 'peripheral-list'
        if (path.includes('/peripheral/detail')) return 'peripheral-list'
        if (path.includes('/ota')) return 'device-ota'
        return 'device-list'
      }
      
      if (path.startsWith('/agent')) return 'agent-list'

      if (path.startsWith('/resource/models')) return 'model-list'
      if (path.startsWith('/resource/quota')) return 'resource-quota'
      if (path.startsWith('/resource/dashboard')) return 'resource-dashboard'
      if (path.startsWith('/resource/report')) return 'resource-report'
      if (path.startsWith('/resource')) return 'resource-dashboard'

      if (path.startsWith('/activity/community')) return 'activity-community'
      if (path.startsWith('/activity/memorial')) return 'activity-memorial'
      if (path.startsWith('/activity/shop/exchange')) return 'activity-exchange'
      if (path.startsWith('/activity/shop')) return 'activity-shop'
      if (path.startsWith('/activity')) return 'activity-community'
      
      return ''
    },
    
    currentModule() {
      const path = this.$route.path
      if (path.startsWith('/analysis')) return this.moduleConfig.analysis
      if (path.startsWith('/user')) return this.moduleConfig.user
      if (path.startsWith('/product/sku-manage')) return this.moduleConfig.sku
      if (path.startsWith('/product/sku-rule')) return this.moduleConfig.skuRule
      if (path.startsWith('/product/series')) return this.moduleConfig.product
      if (path.startsWith('/product')) return this.moduleConfig.product
      if (path.startsWith('/device')) return this.moduleConfig.device
      if (path.startsWith('/device/peripheral')) return this.moduleConfig.peripheral
      if (path.startsWith('/agent')) return this.moduleConfig.agent
      if (path.startsWith('/cms') || path.startsWith('/light')) return { ...this.moduleConfig.cms, path: this.cmsDefaultPath }
      if (path.startsWith('/resource')) return this.moduleConfig.resource
      if (path.startsWith('/activity')) return { title: '活动管理', path: '/activity/community', icon: 'el-icon-trophy' }
      if (path.startsWith('/aftersales')) return { title: '售后服务', path: '/aftersales/consultation', icon: 'el-icon-service' }
      if (path.startsWith('/system')) return this.moduleConfig.system
      return null
    },
    
    currentPage() {
      const route = this.$route
      const pageMap = {
        '/analysis/overview': { title: this.$t('analysis.overview'), path: '/analysis/overview' },
        '/analysis/user': { title: this.$t('analysis.userAnalysis'), path: '/analysis/user' },
        '/analysis/points': { title: this.$t('analysis.pointsAnalysis'), path: '/analysis/points' },
        '/analysis/product': { title: this.$t('analysis.productAnalysis'), path: '/analysis/product' },
        '/analysis/collection': { title: this.$t('analysis.collectionAnalysis'), path: '/analysis/collection' },
        '/analysis/badge': { title: this.$t('analysis.badgeAnalysis'), path: '/analysis/badge' },
        '/analysis/activity': { title: this.$t('analysis.userActivity'), path: '/analysis/activity' },
        '/user': { title: this.$t('layout.userList'), path: '/user' },
        '/user/group': { title: this.$t('layout.userGroup'), path: '/user/group' },
        '/user/role': { title: this.$t('layout.rolePermissions'), path: '/user/role' },
        '/user/permission': { title: this.$t('layout.permissionManagement'), path: '/user/permission' },
        '/product/sku-manage': { title: 'SKU管理', path: '/product/sku-manage' },
        '/product/sku-rule': { title: 'SKU规则', path: '/product/sku-rule' },
        '/product/series': { title: '系列管理', path: '/product/series' },
        '/product': { title: '产品列表', path: '/product' },
        '/device': { title: this.$t('layout.deviceList'), path: '/device' },
        '/device/ota': { title: this.$t('otaManagement.firmwareManagement'), path: '/device/ota' },
        '/device/peripheral/list': { title: 'NFC管理', path: '/device/peripheral/list' },

        '/resource/dashboard': { title: this.$t('layout.resourceOverview'), path: '/resource/dashboard' },
        '/resource/quota': { title: this.$t('layout.quotaManagement'), path: '/resource/quota' },
        '/resource/report': { title: this.$t('layout.usageReport'), path: '/resource/report' },
        '/resource/models': { title: this.$t('layout.modelManagement'), path: '/resource/models' },
        '/resource/provider': { title: this.$t('providerManagement.pageTitle'), path: '/resource/provider' },
        
        '/cms/assets': { title: '资产管理', path: '/cms/assets' },
        '/cms/themes': { title: '主题管理', path: '/cms/themes' },
        '/cms/tags': { title: '标签管理', path: '/cms/tags' },
        '/light/effect': { title: '灯效管理', path: '/light/effect' },
        '/cms/documents': { title: '文档管理', path: '/cms/documents' },

        '/system/log': { title: this.$t('layout.operationLog'), path: '/system/log' },
        '/system/notification': { title: this.$t('layout.notificationManagement'), path: '/system/notification' },
        '/system/alarm': { title: this.$t('layout.alarmManagement'), path: '/system/alarm' },
        '/system/monitor': { title: this.$t('layout.serverMonitor'), path: '/system/monitor' },
        '/system/job': { title: this.$t('layout.jobManagement'), path: '/system/job' },
        '/system/dict': { title: this.$t('dictManagement.pageTitle'), path: '/system/dict' },
        '/system/params': { title: this.$t('paramManagement.pageTitle'), path: '/system/params' },
        '/system/config': { title: this.$t('layout.systemConfig'), path: '/system/config' },
        '/aftersales/consultation': { title: '客诉管理', path: '/aftersales/consultation' }
      }
      
      const mappedPage = pageMap[route.path]
      if (mappedPage) return mappedPage
      
      // 如果没有在 map 中找到，尝试从 route.meta 中获取
      if (route.meta && route.meta.title) {
        return {
          title: route.meta.title,
          path: route.path
        }
      }
      
      return null
    },
    

    cachedViews() {
      return [
        'AnalysisOverview',
        'AnalysisUser',
        'AnalysisProduct',
        'AnalysisCollection',
        'AnalysisBadge',
        'AnalysisUserActivity',
        'AgentList',
        'AgentTemplate',
        'DeviceList',
        'NfcManagement',
        'UserList',
        'UserDetail',
        'ProductList',
        'SkuList',
        'AssetList',
        'CmsThemeList',
        'LightEffectList',
        'ModelList',
        'ConsultationList',
        'DocumentList',
        'ChatHistory',
        'ResourceQuota',
        'ResourceReport',
        'QuotaLogs',
        'QuotaPolicy',
        'SkuRuleList',
        'NotificationManagement',
        'AlarmManagement',
        'DictManagement',
        'JobManagement',
        'logManagementTips',
        'OtaManagement',
        'SystemParams',
        'ProviderManagement',
        'PermissionManagement',
        'TagManagement',
        'ResourceDashboard',
        'ServerMonitor',
        'SystemNotification',
        'SystemConfig',
        'SystemLog',
        'JobLog',
        'NotificationChannel',
        'NotificationTemplate',
        'AgentPlugin',
        'UserRole',
        'Dashboard'
      ]
    },
    
    defaultOpeneds() {
      const path = this.$route.path
      if (path.startsWith('/analysis')) return ['analysis']
      if (path.startsWith('/user')) return ['user']
      if (path.startsWith('/product')) return ['product']
      if (path.startsWith('/device')) return ['device']
      if (path.startsWith('/device/peripheral')) return ['device']
      if (path.startsWith('/agent')) return ['agent']
      if (path.startsWith('/cms')) return ['cms']
      if (path.startsWith('/light')) return ['cms']
      if (path.startsWith('/resource')) return ['resource']
      if (path.startsWith('/activity')) return ['activity']
      if (path.startsWith('/aftersales')) return ['aftersales']
      if (path.startsWith('/system')) return ['system']
      return []
    }
  },
  watch: {
    '$route'(to, from) {
      const el = this.$refs.mainContent ? (this.$refs.mainContent.$el || this.$refs.mainContent) : null
      if (el) {
        if (from && from.path) {
          this._scrollPositions = this._scrollPositions || {}
          this._scrollPositions[from.path] = el.scrollTop
        }
      }
      this.$nextTick(() => {
        const el = this.$refs.mainContent ? (this.$refs.mainContent.$el || this.$refs.mainContent) : null
        if (el) {
          this._scrollPositions = this._scrollPositions || {}
          const saved = this._scrollPositions[to.path]
          if (saved !== undefined) {
            el.scrollTop = saved
          } else {
            el.scrollTop = 0
          }
        }
      })
    },
    sidebarCollapsed(val) {
      if (!val) {
        // 展开时，确保当前激活项所属的子菜单也是展开的
        this.$nextTick(() => {
          const path = this.$route.path
          let activeSubmenu = ''
          if (path.startsWith('/analysis')) activeSubmenu = 'analysis'
          else if (path.startsWith('/user')) activeSubmenu = 'user'
          else if (path.startsWith('/device')) activeSubmenu = 'device'
          else if (path.startsWith('/agent')) activeSubmenu = 'agent'
          else if (path.startsWith('/resource')) activeSubmenu = 'resource'
          else if (path.startsWith('/activity')) activeSubmenu = 'activity'
          else if (path.startsWith('/cms')) activeSubmenu = 'cms'
          else if (path.startsWith('/system')) activeSubmenu = 'system'
          
          if (activeSubmenu && this.$refs.sidebarMenu) {
            this.$refs.sidebarMenu.open(activeSubmenu)
          }
        })
      }
    }
  },
  methods: {
    ...mapActions('user', ['logout', 'refreshCurrentUser']),
    
    hasPermission(permission) {
      if (!this.currentUser) return false
      if (this.currentUser.superAdmin === 1) return true
      const permissions = this.currentUser.permissions || []
      return permissions.some(p => p === '*:*:*' || p === permission)
    },
    hasAnyPermission(permissionList) {
      return permissionList.some(permission => this.hasPermission(permission))
    },
    hasAnyRouteAccess(routeList) {
      return routeList.some(routePath => this.canAccessRoute(routePath))
    },
    canAccessRoute(routePath) {
      if (!this.currentUser) return false
      if (this.currentUser.superAdmin === 1) return true

      const matched = this.$router.match(routePath).matched || []
      const permissions = matched
        .map(route => route.meta && route.meta.permissions)
        .filter(item => Array.isArray(item) && item.length > 0)
        .pop()
      const routeMeta = [...matched].reverse().find(route => route.meta && Array.isArray(route.meta.permissions) && route.meta.permissions.length > 0)
      const matchMode = routeMeta && routeMeta.meta && routeMeta.meta.permissionMode === 'any' ? 'any' : 'all'

      if (!permissions || permissions.length === 0) return true
      if (matchMode === 'any') {
        return this.hasAnyPermission(permissions)
      }
      return permissions.every(permission => this.hasPermission(permission))
    },
    async syncPermissions() {
      if (!this.currentUser || !this.currentUser.id) return
      try {
        await this.refreshCurrentUser()
      } catch (error) {
      }
    },

    getFullUrl,
    
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
      if (this.isMobile) {
        this.sidebarCollapsed = true;
      }
    },
    
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    
    handleMenuSelect(index, indexPath) {
      // 菜单选择处理
    },
    
    handleUserCommand(command) {
      switch (command) {
        case 'profile':
          this.$router.push('/profile')
          break
        case 'settings':
          this.$router.push('/settings')
          break
        case 'logout':
          this.handleLogout()
          break
      }
    },
    
    handlePageAction(action) {
      if (action.handler && typeof action.handler === 'function') {
        action.handler()
      } else if (action.router) {
        this.$router.push(action.router)
      } else if (action.key) {
        // 如果是翻译键，使用$t函数解析
        const translation = this.$t(action.key)
        if (translation !== action.key) {
          action.text = translation
        }
      }
    },
    

    async handleLogout() {
      try {
        await this.logout()
        // 使用 replace 避免在历史记录中留下当前页，并捕获导航重定向错误
        this.$router.replace('/login').catch(err => {
          // 忽略由于导航守卫重定向导致的错误
          if (err && err.name !== 'NavigationDuplicated' && !err.message.includes('Redirected')) {
            console.error('退出登录跳转失败:', err)
          }
        })
        this.$message.success(this.$t('layout.logoutSuccess'))
      } catch (error) {
        this.$message.error(this.$t('layout.logoutError'))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
:export {
  sidebarBg: #304156;
}

.main-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
  
  /* 侧边栏 CSS 变量定义 */
  --sidebar-bg: #304156;
  --sidebar-text: #bfcbd9;
  --sidebar-active-text: #ffffff;
  --sidebar-active-bg: #1890ff;
  --sidebar-hover-bg: rgba(255, 255, 255, 0.08);
  --sidebar-submenu-bg: #1f2d3d;
  --sidebar-submenu-hover-bg: #001528;
  --sidebar-transition: all 0.4s cubic-bezier(0.645, 0.045, 0.355, 1);
  --sidebar-shadow: 2px 0 6px rgba(0, 21, 41, 0.35);
}

/* 侧边栏收缩动画序列控制 */
.main-sidebar {
  transition: width 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  /* 收起状态：先收起文字，延迟后收起宽度 */
  &.sidebar-collapsed {
    transition-delay: 0.15s;
    
    ::v-deep .el-menu-item span,
    ::v-deep .el-submenu__title span {
      opacity: 0;
      transition: opacity 0.15s ease;
    }
  }
  
  /* 展开状态：先展开宽度，延迟后显示文字 */
  &:not(.sidebar-collapsed) {
    transition-delay: 0s;
    
    ::v-deep .el-menu-item span,
    ::v-deep .el-submenu__title span {
      opacity: 1;
      transition: opacity 0.15s ease 0.3s;
    }
  }
}

.main-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  padding: 0 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .header-left {
    flex: 0 0 200px;
    
    .logo-section {
      display: flex;
      align-items: center;
      
      .header-logo {
        height: 32px;
        margin-right: 10px;
      }
      
      .system-title {
        font-size: 18px;
        font-weight: 600;
      }
    }
  }

  .header-center {
    flex: 1;
    
    .breadcrumb-nav {
      text-align: center;
      
      ::v-deep .el-breadcrumb__item .el-breadcrumb__inner {
        color: rgba(255, 255, 255, 0.8);
        
        &:hover {
          color: white;
        }
      }
    }
  }

  .header-right {
    flex: 0 0 200px;
    text-align: right;
    
    .user-dropdown {
      display: flex;
      align-items: center;
      cursor: pointer;
      color: white;
      
      .username {
        margin: 0 8px;
      }
      
      &:hover {
        opacity: 0.8;
      }
    }
  }
}

.main-container {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.sidebar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  backdrop-filter: blur(2px);
  transition: all 0.3s;
}

.main-sidebar {
  background-color: var(--sidebar-bg);
  box-shadow: var(--sidebar-shadow);
  position: relative;
  transition: width var(--sidebar-transition);
  display: flex;
  flex-direction: column;
  z-index: 1001;
  overflow: hidden;
  user-select: none; /* 防止文字被选中 */

  .sidebar-toggle-btn {
    width: 36px;
    height: 36px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--sidebar-text);
    font-size: 16px;
    transition: var(--sidebar-transition);
    z-index: 100;
    border: 1px solid rgba(255, 255, 255, 0.1);
    margin: 20px 0 20px 14px;
    flex-shrink: 0;
    
    &:hover {
      background: var(--sidebar-active-bg);
      color: var(--sidebar-active-text);
      box-shadow: 0 0 12px rgba(24, 144, 255, 0.5);
    }
    
    i {
      transition: transform 0.3s;
    }
  }

  &.sidebar-collapsed {
    .sidebar-toggle-btn {
      margin: 20px auto;
      align-self: center;
    }
  }

  .sidebar-menu {
    border: none;
    background: transparent;
    padding-top: 10px;
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar { display: none; } /* Chrome/Safari */
    
    ::v-deep .el-menu {
      background-color: var(--sidebar-submenu-bg);
      border: none;
    }

    ::v-deep .el-menu-item,
    ::v-deep .el-submenu__title {
      height: 50px;
      line-height: 50px;
      color: var(--sidebar-text) !important;
      margin: 4px 8px;
      border-radius: 8px;
      transition: var(--sidebar-transition);
      
      i {
        color: inherit;
        font-size: 18px;
        margin-right: 12px;
        width: 24px;
        text-align: center;
        flex-shrink: 0;
      }

      span {
        white-space: nowrap;
        overflow: hidden;
      }

      &:hover {
        background-color: var(--sidebar-hover-bg) !important;
        color: var(--sidebar-active-text) !important;
      }
      
      &.is-active {
        background-color: var(--sidebar-active-bg) !important;
        color: var(--sidebar-active-text) !important;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
      }
    }
    
    ::v-deep .el-submenu {
      &.is-active {
        > .el-submenu__title {
          color: var(--sidebar-active-text) !important;
          font-weight: 500;
        }
      }

      .el-menu-item {
        height: 40px;
        line-height: 40px;
        margin: 2px 8px 2px 20px;
        font-size: 13px;
        
        &.is-active {
          background-color: var(--sidebar-active-bg) !important;
        }
      }
    }

    /* 折叠状态下的特殊处理 */
    &.el-menu--collapse {
      ::v-deep .el-menu-item,
      ::v-deep .el-submenu__title {
        margin: 4px 0;
        padding: 0 !important;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        
        i {
          margin: 0 !important;
          font-size: 20px;
          transition: none;
        }
        
        /* 针对 Element UI 自动添加的 tooltip 的重置 */
        ::v-deep .el-tooltip {
          padding: 0 !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }

        span {
          display: none;
        }

        .el-submenu__icon-arrow {
          display: none !important;
        }
      }
    }
  }
}

.main-content {
  background: #f5f7fa;
  padding: 20px;
  overflow: hidden; /* 禁止全局滚动 */
  display: flex;
  flex-direction: column;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Edge */
  }
  
  .content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-shrink: 0; /* 头部固定 */
    
    .page-title {
      margin: 0;
      color: #2c3e50;
      font-size: 24px;
      font-weight: 600;
    }
    
    .page-actions {
      display: flex;
      gap: 10px;
    }
  }
  
  .content-body {
    flex: 1; /* 占满剩余空间 */
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari/Edge */
    }
  }
}

@media (max-width: 768px) {
  .main-header {
    padding: 0 10px;
    
    .header-left .logo-section .system-title {
      display: none;
    }
    
    .header-center {
      display: none;
    }
  }
  
  .main-sidebar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 1001;
    transform: translateX(-100%);
    transition: transform var(--sidebar-transition), width var(--sidebar-transition);
    
    &.show {
      transform: translateX(0);
    }
  }
  
  .main-content {
    padding: 10px;
  }
}
</style>
